// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;
let fedRawData;
let fedChartData;

const margins = {
  top: 20,
  right: 90,
  bottom: 25,
  left: 35,
};

const parser = d3.timeParse('%Y-%m-%d'); // https://github.com/d3/d3-time-format;

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let fedLastData;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%m/%d/%Y');
const recessionData = [
  { start: '7/1/1990', end: '3/1/1991' },
  { start: '3/1/2001', end: '11/1/2001' },
  { start: '12/1/2007', end: '6/1/2009' },
  { start: '2/1/2020', end: '4/1/2020' },
];

recessionData.forEach((d) => {
  d.start = rParser(d.start);
  d.end = rParser(d.end);
});

Promise.all([
  d3.json('https://ix.cnn.io/data/mortgage-rates/fred-30-year-fixed-mortgage-rates.json'),
  d3.json('https://ix.cnn.io/data/interest-rates/interest_rates.json'),
]).then(([mortgageData, fedData]) => {
  rawData = mortgageData.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    value: +d.value,
  }));

  // FEDFUNDS target rate series
  const fedSeries = (fedData && fedData.FEDFUNDS && fedData.FEDFUNDS.data) ? fedData.FEDFUNDS.data : [];
  fedRawData = fedSeries.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    value: +d.target_rate,
  }));

  const minTime = pTime('2021-01-01');
  chartData = rawData.filter(d => d.date > minTime);
  fedChartData = fedRawData.filter(d => d.date > minTime);

  // x domain across both series
  minDomain = d3.min([d3.min(chartData, d => d.date), d3.min(fedChartData, d => d.date)]);
  maxDomain = d3.max([d3.max(chartData, d => d.date), d3.max(fedChartData, d => d.date)]);

  // y domain across both series (start at 0)
  minValue = 0;
  maxValue = d3.max([d3.max(chartData, d => d.value) || 0, d3.max(fedChartData, d => d.value) || 0]);
  maxValue = 8.25;

  lastData = chartData[chartData.length - 1];
  fedLastData = fedChartData[fedChartData.length - 1];

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 5 : 5;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%b ’%y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => (i == x.length - 1 ? `${d3.format('.0f')(d)}%` : `${d3.format('.0f')(d)}`));

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const pathFed = d3.line()
    .curve(d3.curveStepAfter)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  // Highlight period starting Nov. 8, 2024 through end of domain
  const highlightStart = pTime('2024-09-19');
  const startX = xScale(highlightStart < minDomain ? minDomain : highlightStart);
  const highlightWidth = Math.max(0, chartWidth - startX);

  const highlight = g.append('g')
    .attr('class', 'highlight');

  // highlight.append('rect')
  //   .attr('x', startX)
  //   .attr('y', 0)
  //   .attr('width', highlightWidth)
  //   .attr('height', chartHeight)
  //   .attr('fill', '#e3e3e3')
  //   .attr('opacity', 0.5);

  // Vertical annotation line at highlight start
  highlight.append('line')
    .attr('x1', startX)
    .attr('x2', startX)
    .attr('y1', 0)
    .attr('y2', chartHeight)
    .attr('stroke', '#999')
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', '3,3');

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#7c4ea5')
    .attr('stroke-width', '2')
    .attr('fill', 'none')
    .attr('d', path);

  // Fed funds target rate line
  line.append('path')
    .datum(fedChartData)
    .attr('stroke', d => '#f18851')
    .attr('stroke-width', '2')
    .attr('fill', 'none')
    .attr('d', pathFed);

  d3.select('.grid.y .domain').remove();

  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

  annotation.append('circle')
    .attr('r', '4')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text(d3.timeFormat('%b %-d, %Y')(lastData.date));

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text(`${d3.format('.2f')(lastData.value)}%`)
    .attr('dy', '1.2em');

  // Fed annotation
  if (fedLastData) {
    const fedAnnot = g.append('g')
      .attr('class', 'annotation fed')
      .attr('transform', `translate(${[xScale(fedLastData.date), yScale(fedLastData.value)]})`);

    fedAnnot.append('circle')
      .attr('r', '4')
      .attr('fill', '#f18851')
      .attr('stroke', 'none');

    fedAnnot.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date')
      .text(d3.timeFormat('%b %-d, %Y')(fedLastData.date));

    fedAnnot.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-value')
      .text(`${d3.format('.3f')(fedLastData.value)}%`)
      .attr('dy', '1.2em');
  }

  // Highlight label, centered within the shaded region at ~5.5%
  const startMs = Math.max(+highlightStart, +minDomain);
  const endMs = +maxDomain;
  const midTime = new Date((startMs + endMs) / 2);
  const labelX = xScale(startMs);
  const labelY = yScale(5.6);

  g.append('text')
    .attr('class', 'highlight-label')
    .attr('x', labelX + 10)
    .attr('y', labelY)
    .attr('text-anchor', 'begin')
    .attr('font-size', 12)
    .style('font-style', 'italic')
    .style('fill', '#555')
    .text('Rates diverged');

  // // Legend
  // const legend = g.append('g')
  //   .attr('class', 'legend')
  //   .attr('transform', `translate(${[chartWidth - 10, 0]})`);

  // const legendItems = [
  //   { label: '30-year fixed mortgage', color: '#7c4ea5' },
  //   { label: 'Federal funds target rate', color: '#2171b5' },
  // ];

  // const legendEntry = legend.selectAll('g.entry')
  //   .data(legendItems)
  //   .enter()
  //   .append('g')
  //   .attr('class', 'entry')
  //   .attr('transform', (d, i) => `translate(${-160}, ${i * 18})`);

  // legendEntry.append('rect')
  //   .attr('width', 16)
  //   .attr('height', 3)
  //   .attr('y', 6)
  //   .attr('fill', d => d.color);

  // legendEntry.append('text')
  //   .text(d => d.label)
  //   .attr('x', 22)
  //   .attr('y', 9)
  //   .attr('dy', '.35em');

  window.pymChild.sendHeight();
}
